export type FieldType =
  | "text"
  | "richtext"
  | "number"
  | "date"
  | "select"
  | "multiselect"
  | "user"
  | "checkbox"
  | "url"
  | "email"
  | "phone"
  | "channel"
  | "file"
  | "message";

export type SelectOption = {
  id: string;
  label: string;
  color?: string;
  icon?: string;
};

export type FieldDefinition = {
  id: string;
  name: string;
  type: FieldType;
  required?: boolean;
  position: number;
  width?: number;
  /** When true, the field is hidden from the list grid but still visible in the detail panel. */
  hidden?: boolean;
  config?: {
    options?: SelectOption[];
    defaultValue?: FieldValue;
    placeholder?: string;
  };
};

export type FieldValue = string | number | boolean | string[] | null;

export type DefaultSort = {
  fieldId: string;
  direction: "asc" | "desc";
} | null;

const VALID_FIELD_TYPES: ReadonlySet<string> = new Set<FieldType>([
  "text",
  "richtext",
  "number",
  "date",
  "select",
  "multiselect",
  "user",
  "checkbox",
  "url",
  "email",
  "phone",
  "channel",
  "file",
  "message",
]);

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------

export const DEFAULT_TEMPLATE: FieldDefinition[] = [
  {
    id: "status",
    name: "Status",
    type: "select",
    position: 0,
    config: {
      defaultValue: "open",
      options: [
        { id: "open", label: "Open", color: "#00ffff", icon: "Circle" },
        {
          id: "in_progress",
          label: "In Progress",
          color: "#facc15",
          icon: "CircleHalf",
        },
        {
          id: "closed",
          label: "Closed",
          color: "#39ff14",
          icon: "CheckCircle",
        },
      ],
    },
  },
  { id: "__id", name: "ID", type: "text", position: 1 },
  {
    id: "priority",
    name: "Priority",
    type: "select",
    position: 2,
    config: {
      defaultValue: "medium",
      options: [
        {
          id: "critical",
          label: "Critical",
          color: "#f87171",
          icon: "Warning",
        },
        { id: "high", label: "High", color: "#fb923c", icon: "ArrowUp" },
        { id: "medium", label: "Medium", color: "#facc15", icon: "Minus" },
        { id: "low", label: "Low", color: "#a3a3a3", icon: "ArrowDown" },
      ],
    },
  },
  {
    id: "title",
    name: "Title",
    type: "text",
    position: 3,
    config: { placeholder: "Item name" },
  },
  {
    id: "description",
    name: "Description",
    type: "text",
    position: 4,
    hidden: true,
  },
  {
    id: "tags",
    name: "Tags",
    type: "multiselect",
    position: 5,
    config: {
      options: [
        { id: "bug", label: "Bug", color: "#f87171" },
        { id: "feature", label: "Feature", color: "#00ffff" },
        { id: "improvement", label: "Improvement", color: "#39ff14" },
        { id: "marketing", label: "Marketing", color: "#d8b4fe" },
      ],
    },
  },
  {
    id: "assignee",
    name: "Assignee",
    type: "user",
    position: 6,
  },
  { id: "__created_at", name: "Date Created", type: "date", position: 7 },
];

export const BLANK_TEMPLATE: FieldDefinition[] = [
  {
    id: "title",
    name: "Title",
    type: "text",
    position: 0,
  },
];

/** @deprecated Use DEFAULT_TEMPLATE instead */
export const TASKS_TEMPLATE: FieldDefinition[] = DEFAULT_TEMPLATE;

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/**
 * Validates `field_definitions` array structure in isolation.
 * Returns an error message or null if valid.
 *
 * Context-dependent checks (immutability of id/type, select option IDs,
 * new required fields on non-empty lists) are enforced by mutators that
 * compare against existing definitions.
 */
export function validateFieldDefinitions(
  defs: FieldDefinition[],
): string | null {
  if (!Array.isArray(defs) || defs.length === 0) {
    return "field_definitions must be a non-empty array";
  }

  if (!defs.some((d) => d.type === "text")) {
    return "field_definitions must include at least one text field";
  }

  const ids = new Set<string>();

  for (const def of defs) {
    if (!def.id || typeof def.id !== "string") {
      return "Each field definition must have a non-empty string id";
    }
    if (ids.has(def.id)) {
      return `Duplicate field id: ${def.id}`;
    }
    ids.add(def.id);

    if (!def.name || typeof def.name !== "string") {
      return `Field "${def.id}" must have a non-empty string name`;
    }
    if (!VALID_FIELD_TYPES.has(def.type)) {
      return `Field "${def.id}" has invalid type: ${def.type}`;
    }
    if (typeof def.position !== "number") {
      return `Field "${def.id}" must have a numeric position`;
    }

    // Select/multiselect must have options
    if (def.type === "select" || def.type === "multiselect") {
      const options = def.config?.options;
      if (!options || !Array.isArray(options) || options.length === 0) {
        return `Field "${def.id}" (${def.type}) must have at least one option`;
      }
      const optionIds = new Set<string>();
      for (const opt of options) {
        if (!opt.id || typeof opt.id !== "string") {
          return `Field "${def.id}" has an option with invalid id`;
        }
        if (optionIds.has(opt.id)) {
          return `Field "${def.id}" has duplicate option id: ${opt.id}`;
        }
        optionIds.add(opt.id);
        if (!opt.label || typeof opt.label !== "string") {
          return `Field "${def.id}" option "${opt.id}" must have a label`;
        }
      }
    }

    // Validate defaultValue if present
    if (def.config?.defaultValue !== undefined) {
      const err = validateFieldValue(def, def.config.defaultValue);
      if (err) {
        return `Field "${def.id}" has invalid defaultValue: ${err}`;
      }
    }
  }

  return null;
}

/**
 * Validates a single field value against its definition.
 * Returns an error message or null if valid.
 */
export function validateFieldValue(
  definition: FieldDefinition,
  value: FieldValue,
): string | null {
  // Null is allowed unless required
  if (value === null || value === undefined) {
    if (definition.required) {
      return `Field "${definition.id}" is required`;
    }
    return null;
  }

  switch (definition.type) {
    case "text":
    case "richtext":
    case "url":
    case "file":
    case "message":
      if (typeof value !== "string") {
        return `Field "${definition.id}" must be a string`;
      }
      if (definition.type === "text" && (value as string).length > 1000) {
        return `Field "${definition.id}" exceeds 1000 character limit`;
      }
      if (definition.required && (value as string).length === 0) {
        return `Field "${definition.id}" is required`;
      }
      return null;

    case "email":
      if (typeof value !== "string") {
        return `Field "${definition.id}" must be a string`;
      }
      if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        return `Field "${definition.id}" must be a valid email address`;
      }
      if (definition.required && value.length === 0) {
        return `Field "${definition.id}" is required`;
      }
      return null;

    case "phone":
      if (typeof value !== "string") {
        return `Field "${definition.id}" must be a string`;
      }
      if (value.length > 30) {
        return `Field "${definition.id}" exceeds 30 character limit`;
      }
      if (definition.required && value.length === 0) {
        return `Field "${definition.id}" is required`;
      }
      return null;

    case "channel":
      if (typeof value !== "string") {
        return `Field "${definition.id}" must be a string (channel ID)`;
      }
      return null;

    case "number":
      if (typeof value !== "number" || !Number.isFinite(value)) {
        return `Field "${definition.id}" must be a finite number`;
      }
      return null;

    case "date":
      if (typeof value !== "number" || !Number.isFinite(value)) {
        return `Field "${definition.id}" must be a number (epoch ms)`;
      }
      return null;

    case "checkbox":
      if (typeof value !== "boolean") {
        return `Field "${definition.id}" must be a boolean`;
      }
      return null;

    case "user":
      if (typeof value !== "string") {
        return `Field "${definition.id}" must be a string (user ID)`;
      }
      return null;

    case "select": {
      if (typeof value !== "string") {
        return `Field "${definition.id}" must be a string (option ID)`;
      }
      const options = definition.config?.options ?? [];
      if (!options.some((o) => o.id === value)) {
        return `Field "${definition.id}" has invalid option: ${value}`;
      }
      return null;
    }

    case "multiselect": {
      if (!Array.isArray(value)) {
        return `Field "${definition.id}" must be an array of option IDs`;
      }
      if (definition.required && value.length === 0) {
        return `Field "${definition.id}" is required`;
      }
      const options = definition.config?.options ?? [];
      const validIds = new Set(options.map((o) => o.id));
      for (const v of value) {
        if (typeof v !== "string") {
          return `Field "${definition.id}" array elements must be strings`;
        }
        if (!validIds.has(v)) {
          return `Field "${definition.id}" has invalid option: ${v}`;
        }
      }
      return null;
    }

    default:
      return `Unknown field type: ${definition.type}`;
  }
}

/**
 * Validates an item's `fields` object against the list's `field_definitions`.
 *
 * Before calling this, the mutator should:
 * 1. Strip keys from `fields` that no longer exist in `fieldDefinitions`
 * 2. Apply `config.defaultValue` for required fields missing from `fields`
 *
 * Returns an error message or null if valid.
 */
export function validateItemFields(
  fields: Record<string, FieldValue>,
  fieldDefinitions: FieldDefinition[],
): string | null {
  const defMap = new Map(fieldDefinitions.map((d) => [d.id, d]));

  // Reject unknown keys
  for (const key of Object.keys(fields)) {
    if (!defMap.has(key)) {
      return `Unknown field: ${key}`;
    }
  }

  // Validate each field definition
  for (const def of fieldDefinitions) {
    if (def.id.startsWith("__")) continue; // Sentinel fields read from item-level columns, not JSONB
    const value = def.id in fields ? (fields[def.id] as FieldValue) : undefined;
    // Treat missing key as null for validation
    const err = validateFieldValue(def, value ?? null);
    if (err) return err;
  }

  return null;
}

// ---------------------------------------------------------------------------
// Position helpers
// ---------------------------------------------------------------------------

export function getInsertPosition(items: { position: number }[]): number {
  if (items.length === 0) return 1;
  return Math.max(...items.map((i) => i.position)) + 1;
}

export function getMidPosition(before: number, after: number): number {
  return (before + after) / 2;
}

// ---------------------------------------------------------------------------
// Prefix helpers
// ---------------------------------------------------------------------------

export function generatePrefix(name: string): string {
  return name
    .replace(/[^a-zA-Z]/g, "")
    .slice(0, 3)
    .toUpperCase()
    .padEnd(3, "X");
}

export function validatePrefix(prefix: string): string | null {
  if (!/^[A-Z]{2,5}$/.test(prefix)) {
    return "Prefix must be 2-5 uppercase letters";
  }
  return null;
}
